import { fetchWithTimeout, HttpError } from "../utils/fetchWithTimeout";
import type {
  LocalWithdrawalData,
} from "../types/offlineWithdrawal";

export interface SyncCreateResponse {
  created: boolean;

  withdrawal_id: number;
  client_uuid: string;

  withdrawal_number:
    number | null;

  owner_user_id: number;
  owner_username: string;

  current_version: number;

  sync_status: string;
}



export interface SyncStatusResponse {
  withdrawal_id: number;
  client_uuid: string;
  withdrawal_number: number | null;
  owner_user_id: number;
  current_version: number;
  sync_status: string;
  pdf_present: boolean;
  preview_present: boolean;
  label_present: boolean;
  missing_files: string[];
  synced_at: string | null;
  email_job_id: number | null;
  email_status: string | null;
}


export interface WithdrawalServerFile {
  id: number;
  version: number;
  file_type: string;
  original_filename: string;
  mime_type: string;
  created_at: string;
}

export async function listWithdrawalServerFiles(withdrawalId: number): Promise<WithdrawalServerFile[]> {
  const response = await fetchWithTimeout(`/api/withdrawals/${withdrawalId}/files`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    cache: "no-store",
  }, 5000);
  if (!response.ok) throw new HttpError(response.status, await parseError(response));
  return response.json();
}

export interface SyncCompleteResponse {
  withdrawal_id: number;
  client_uuid: string;

  sync_status: string;
  synced_at: string;

  email_queued: boolean;

  email_job_id:
    number | null;

  email_status:
    string | null;

  email_error:
    string | null;
}

interface FastApiValidationDetail {
  loc?: Array<string | number>;
  msg?: string;
  type?: string;
}

const fieldLabels: Record<string, string> = {
  withdrawal_date: "Entnahmedatum",
  material_number: "Materialnummer",
  material_text: "Bezeichnung",
  material_type: "Typ",
  building: "Gebäude",
  floor: "Ebene",
  cabinet: "Lagerort",
  compartment: "Feld",
  sub_compartment: "Fach",
  quantity_removed: "Menge",
  current_stock: "Lagerbestand",
  accounting_system: "Anlage",
  accounting_subarea: "Anlagenbereich",
  accounting_number: "Kontierung",
  order_info: "Bestellinfo",
  persons: "Entnommen durch",
};

function validationDetailText(detail: FastApiValidationDetail): string {
  const key = [...(detail.loc ?? [])].reverse().find((part) => typeof part === "string" && part !== "body");
  const label = typeof key === "string" ? (fieldLabels[key] ?? key) : "Eingabe";
  return `${label}: ${detail.msg ?? "Ungültiger Wert."}`;
}

async function parseError(
  response: Response
): Promise<string> {
  try {
    const body = await response.json() as { detail?: string | FastApiValidationDetail[] };

    if (typeof body?.detail === "string") return body.detail;

    if (Array.isArray(body?.detail) && body.detail.length > 0) {
      return body.detail.map(validationDetailText).join(" · ");
    }
  } catch {
    // Ignorieren
  }

  return `Serverfehler ${response.status}`;
}

function optionalText(value: string | null | undefined): string | null {
  const clean = value?.trim() ?? "";
  return clean || null;
}

function normalizeSyncData(data: LocalWithdrawalData): LocalWithdrawalData {
  return {
    ...data,
    capture_mode: data.capture_mode ?? "automatic",
    client_uuid: data.client_uuid.trim(),
    withdrawal_date: data.withdrawal_date.trim(),
    material_number: data.material_number.replace(/\s+/g, "").trim(),
    material_text: data.material_text.trim(),
    material_type: optionalText(data.material_type),
    building: data.building.trim(),
    floor: data.floor.trim().toUpperCase(),
    cabinet: data.cabinet.trim(),
    compartment: data.compartment.trim(),
    sub_compartment: data.sub_compartment.trim(),
    quantity_removed: data.quantity_removed.trim(),
    current_stock: data.current_stock.trim(),
    accounting_system: optionalText(data.accounting_system),
    accounting_subarea: optionalText(data.accounting_subarea),
    // Technische Normalform bleibt ohne Leerzeichen; sichtbar wird zentral formatiert.
    accounting_manufacturer: optionalText(data.accounting_manufacturer),
    accounting_number: optionalText(data.accounting_number)?.replace(/\s+/g, "") ?? null,
    order_info: optionalText(data.order_info),
    remark: optionalText(data.remark),
    persons: data.persons.map((person) => ({ name: person.name.trim() })).filter((person) => Boolean(person.name)),
  };
}

function validateSyncData(data: LocalWithdrawalData): void {
  const failures: string[] = [];
  if (!/^\d{10}$/.test(data.material_number)) failures.push("Materialnummer muss aus genau 10 Ziffern bestehen.");
  if (!data.material_text) failures.push("Bezeichnung darf nicht leer sein.");
  if (!/^\d{2}$/.test(data.building)) failures.push("Gebäude muss aus genau 2 Ziffern bestehen.");
  if (!/^[A-Z]$/.test(data.floor)) failures.push("Ebene muss aus genau einem Buchstaben bestehen.");
  if (!/^\d{3}$/.test(data.cabinet)) failures.push("Lagerort muss aus genau 3 Ziffern bestehen.");
  if (!/^\d{2}$/.test(data.compartment)) failures.push("Feld muss aus genau 2 Ziffern bestehen.");
  if (!/^\d{2}$/.test(data.sub_compartment)) failures.push("Fach muss aus genau 2 Ziffern bestehen.");
  if (!data.quantity_removed || data.quantity_removed.length > 50) failures.push("Menge muss ausgefüllt sein und darf höchstens 50 Zeichen enthalten.");
  if (!data.current_stock || data.current_stock.length > 50) failures.push("Lagerbestand muss ausgefüllt sein und darf höchstens 50 Zeichen enthalten.");
  if (data.persons.length === 0) failures.push("Mindestens eine Person muss bei „Entnommen durch“ ausgewählt sein.");
  if (data.persons.some((person) => person.name.length > 150)) failures.push("Ein Name bei „Entnommen durch“ ist länger als 150 Zeichen.");
  if ((data.material_type?.length ?? 0) > 255) failures.push("Typ ist länger als 255 Zeichen.");
  if ((data.accounting_system?.length ?? 0) > 255) failures.push("Anlage ist länger als 255 Zeichen.");
  if ((data.accounting_subarea?.length ?? 0) > 255) failures.push("Anlagenbereich ist länger als 255 Zeichen.");
  if ((data.accounting_number?.length ?? 0) > 100) failures.push("Kontierung ist länger als 100 Zeichen.");
  if ((data.order_info?.length ?? 0) > 255) failures.push("Bestellinfo ist länger als 255 Zeichen.");
  if (failures.length > 0) throw new Error(`Synchronisation nicht möglich: ${failures.join(" ")}`);
}


export async function getWithdrawalSyncStatus(
  clientUuid: string
): Promise<SyncStatusResponse | null> {
  const response = await fetchWithTimeout(`/api/sync/withdrawals/${clientUuid}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
    cache: "no-store",
  }, 5000);

  if (response.status === 404) return null;
  if (!response.ok) throw new HttpError(response.status, await parseError(response));
  return response.json();
}


export async function syncWithdrawalMetadata(
  data: LocalWithdrawalData & { requested_withdrawal_number?: number | null }
): Promise<SyncCreateResponse> {
  const normalized = normalizeSyncData(data);
  validateSyncData(normalized);
  const requestedWithdrawalNumber = data.requested_withdrawal_number ?? null;

  const response = await fetchWithTimeout(
    "/api/sync/withdrawals",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ ...normalized, requested_withdrawal_number: requestedWithdrawalNumber }),
    },
    5000,
  );

  if (!response.ok) throw new HttpError(response.status, await parseError(response));
  return response.json();
}


export async function syncWithdrawalVersion(
  clientUuid: string,
  baseVersion: number,
  data: LocalWithdrawalData & { change_description?: string | null }
): Promise<SyncCreateResponse> {
  const normalized = normalizeSyncData(data);
  validateSyncData(normalized);
  const response = await fetchWithTimeout(`/api/sync/withdrawals/${clientUuid}/versions`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      ...normalized,
      change_description: data.change_description ?? null,
      client_uuid: clientUuid,
      base_version: baseVersion,
    }),
  }, 5000);
  if (!response.ok) throw new HttpError(response.status, await parseError(response));
  return response.json();
}


async function normalizeUploadBlob(value: Blob, filename: string): Promise<Blob> {
  const fallbackType = filename.toLowerCase().endsWith(".jpg") || filename.toLowerCase().endsWith(".jpeg")
    ? "image/jpeg"
    : filename.toLowerCase().endsWith(".png")
      ? "image/png"
      : filename.toLowerCase().endsWith(".webp")
        ? "image/webp"
        : filename.toLowerCase().endsWith(".pdf")
          ? "application/pdf"
          : filename.toLowerCase().endsWith(".json")
            ? "application/json"
            : "application/octet-stream";

  // Safari/WebKit kann aus IndexedDB wiederhergestellte File/Blob-Objekte in einer
  // anderen Realm-Identität liefern. Für Multipart bauen wir deshalb bewusst einen
  // frischen Blob aus den echten Bytes, statt uns auf instanceof Blob zu verlassen.
  const candidate = value as Blob & { arrayBuffer?: () => Promise<ArrayBuffer>; type?: string };
  if (typeof candidate?.arrayBuffer === "function") {
    const bytes = await candidate.arrayBuffer();
    return new Blob([bytes], { type: candidate.type || fallbackType });
  }

  throw new Error(`Lokale Datei „${filename}“ kann nicht als Binärdatei gelesen werden.`);
}

export async function uploadWithdrawalFile(
  withdrawalId: number,
  fileType: "pdf" | "pdf_preview" | "label" | "source" | "detection_source" | "analysis_input" | "rectification_input" | "ocr_warp" | "vision_feedback" | "debug" | "debug_data" | "live_training",
  blob: Blob,
  filename: string
): Promise<void> {
  const uploadBlob = await normalizeUploadBlob(blob, filename);
  const formData = new FormData();
  formData.append("file", uploadBlob, filename);

  const response = await fetchWithTimeout(
    `/api/withdrawals/${withdrawalId}/files/${fileType}`,
    {
      method: "POST",
      credentials: "include",
      body: formData,
    },
    30_000,
  );

  if (!response.ok) throw new HttpError(response.status, await parseError(response));
}



export async function uploadWithdrawalFileRaw(
  withdrawalId: number,
  fileType: "pdf" | "pdf_preview" | "label" | "source" | "detection_source" | "analysis_input" | "rectification_input" | "ocr_warp" | "vision_feedback" | "debug" | "debug_data" | "live_training",
  blob: Blob,
  filename: string
): Promise<void> {
  const uploadBlob = await normalizeUploadBlob(blob, filename);
  const response = await fetchWithTimeout(`/api/withdrawals/${withdrawalId}/files/${fileType}/raw`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": uploadBlob.type || "application/octet-stream",
      "X-Filename": filename,
      Accept: "application/json",
    },
    body: uploadBlob,
  }, 30_000);
  if (!response.ok) throw new HttpError(response.status, await parseError(response));
}

export async function completeWithdrawalSync(
  clientUuid: string,
  options: { diagnosticEmailTest?: boolean } = {},
): Promise<SyncCompleteResponse> {
  const response = await fetchWithTimeout(
    `/api/sync/withdrawals/${clientUuid}/complete`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(options.diagnosticEmailTest ? { "X-Diagnostic-Email-Test": "1" } : {}),
      },
    },
    5000,
  );

  if (!response.ok) throw new HttpError(response.status, await parseError(response));
  return response.json();
}
